import { motion, AnimatePresence } from 'framer-motion'
import { CaretLeft } from 'phosphor-react'
import { type FormEvent, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Button } from './Button'
import { Checkbox } from './FormElements/Checkbox'
import { SearchInput } from './SearchInput'
import { type Column } from './Table'
import { Medias } from '../enumns/medias'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { useOnClickOutside } from '../hooks/useOnClickOutside'
import { toast } from 'react-toastify'
import { TOAST_ERROR_DEFAULT_OPTIONS } from '../constants/toast'

type ConfigureTableColumnsMenuProps = {
  isOpen: boolean
  columns: Column[]
  onClose: () => void
  onConfigureColumns: (columns: Column[]) => void
}

export const ConfigureTableColumnsMenu = ({
  isOpen,
  columns,
  onClose,
  onConfigureColumns,
}: ConfigureTableColumnsMenuProps) => {
  const [columnsToManipulate, setColumnsToManipulate] = useState(columns)

  const isTabletSize = useMediaQuery(`(min-width: ${Medias.MD}px)`)
  const tableColumnsMenuEl = useRef(null)

  useOnClickOutside(tableColumnsMenuEl, onClose)

  const handleApplyChangesToColumns = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formValues = Array.from<HTMLInputElement>(
      (event?.target as any).columns
    )

    const updatedColumns: Column[] = columns.map((column) => {
      const formValue = formValues.find(({ id }) => id === column.field)

      if (formValue) {
        return {
          ...column,
          isVisible: formValue.checked,
        }
      }

      return column
    })

    const isEmptyVisibleColumns =
      updatedColumns.filter(({ isVisible }) => isVisible).length === 0

    if (isEmptyVisibleColumns) {
      toast.error(
        'É necessário que pelo menos uma coluna esteja selecionada.',
        TOAST_ERROR_DEFAULT_OPTIONS
      )
      return
    }

    onConfigureColumns(updatedColumns)
    setColumnsToManipulate(updatedColumns)
    onClose()
  }

  const handleSearchColumn = (value: string) => {
    setColumnsToManipulate(
      columns.filter((column) => {
        return column.headerName
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase())
      })
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {isTabletSize ? (
            <div
              ref={tableColumnsMenuEl}
              className="border-primary-light border mt-1 shadow-lg absolute z-50 w-64 p-4 bg-white rounded-lg flex flex-col gap-2.5"
              style={{ zIndex: 9999 }}
            >
              <div>
                <SearchInput
                  placeholder="Buscar"
                  onSearch={handleSearchColumn}
                />
              </div>

              <form onSubmit={handleApplyChangesToColumns}>
                <fieldset className="pb-4 space-y-4 overflow-y-scroll max-h-64 scrollbar">
                  {columnsToManipulate.map(
                    ({ field, headerName, isVisible = true }) => (
                      <Checkbox
                        name="columns"
                        id={field}
                        key={field}
                        label={headerName}
                        defaultChecked={isVisible}
                      />
                    )
                  )}
                </fieldset>

                <div className="mt-2 before:w-[calc(100%-12px)] relative before:absolute before:top-[-2rem] before:left-0 before:h-8 before:bg-gradient-to-t from-white to-transparent">
                  <Button
                    size="small"
                    fullWidth
                    type="submit"
                    className="mx-auto w-44"
                  >
                    Aplicar
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <>
              {createPortal(
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed top-0 bottom-0 left-0 right-0 z-50 p-6 overflow-y-auto bg-white scrollbar"
                  style={{ zIndex: 9999 }}
                >
                  <button
                    aria-label="Voltar"
                    className="mb-8"
                    onClick={() => onClose()}
                  >
                    <CaretLeft size={24} className="text-grey-700" />
                  </button>

                  <>
                    <strong className="block mb-8 text-grey-900">
                      Configurar tabela
                    </strong>

                    <form onSubmit={handleApplyChangesToColumns}>
                      <fieldset className="mb-6 space-y-4">
                        {columnsToManipulate.map(
                          ({ field, headerName, isVisible = true }) => (
                            <Checkbox
                              name="columns"
                              id={field}
                              key={field}
                              label={headerName}
                              defaultChecked={isVisible}
                            />
                          )
                        )}
                      </fieldset>

                        <Button type="submit" fullWidth>
                          Aplicar
                        </Button>
                    </form>
                  </>
                </motion.div>,
                document.body
              )}
            </>
          )}
        </>
      )}
    </AnimatePresence>
  )
}
